/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography
} from '@mui/material';
import AlertDialogSlide from 'common/components/dialog/dialog';
import { reduxForm, getFormValues } from 'redux-form';
import { connect } from 'react-redux';
import { compose } from 'redux';
import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';
import DadosPessoais from './DadosPessoais';
import DadosParceiro from './DadosParceiro';
import Valores from './Valores';
import Confirmacao from './Confirmacao';
import LinkGeradoModal from './LinkGeradoModal';
import { Field } from 'redux-form';
import { upsertLinkPagamentoActivity } from '../linkPagamentoActions';
import { getUserStations } from '../../helpers/clubsMultiStation';
import SelectMultipleV4 from '../../vendasOnlineParceiro/components/SelectMultipleV4';

const steps = ['Dados do cliente', 'Dados parceiro', 'Valores', 'Confirmação'];

const getAvailableStationIds = (user) => {
  const establishments = user?.establishments || [];
  const stationIds = [];

  establishments.forEach((establishment) => {
    (establishment?.stations || []).forEach((station) => {
      if (station?.id) {
        stationIds.push(station.id);
      }
    });
  });

  return Array.from(new Set(stationIds));
};

const LinkPagamentoModal = ({
  visible,
  onClose,
  formValues,
  change,
  handleSubmit,
  onClickProgress,
  editMode,
  stationId,
  stationAccount,
  establishmentId,
  availableStationIds,
  activityId,
  auth,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [stationAccountDetails, setStationAccountDetails] = useState(null);
  const [showLinkGeradoModal, setShowLinkGeradoModal] = useState(false);
  const [generatedLinkData, setGeneratedLinkData] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [showBatchResultDialog, setShowBatchResultDialog] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Buscar detalhes da estação incluindo taxa de administração
  useEffect(() => {
    const fetchStationDetails = async () => {
      if (!stationId && !establishmentId) {
        setStationAccountDetails(stationAccount);
        return;
      }
      
      try {
        const token = JSON.parse(localStorage.getItem(USER_TOKEN));
        const headers = {
          Authorization: `${token}`,
        };

        const requests = [
          stationId
            ? axios.get(`${BASE_API}/stations/${stationId}`, { headers })
            : Promise.resolve({ data: null }),
          establishmentId
            ? axios.get(`${BASE_API}/gateway-digital-accounts/payment-link-accounts/${establishmentId}`, {
                headers,
              })
            : Promise.resolve({ data: [] }),
        ];

        const [stationResponse, accountResponse] = await Promise.all(requests);
        const paymentLinkAccount = Array.isArray(accountResponse.data) ? accountResponse.data[0] : null;
        const administrationTax = stationResponse.data?.administration_tax || 0;
        const resolvedAccount = paymentLinkAccount || stationAccount || null;

        if (resolvedAccount) {
          setStationAccountDetails({
            ...resolvedAccount,
            administration_tax: administrationTax,
          });
          return;
        }

        setStationAccountDetails(null);
      } catch (error) {
        console.error('Erro ao buscar detalhes da estação:', error);
        setStationAccountDetails(stationAccount);
      }
    };

    if (visible && (stationId || establishmentId)) {
      fetchStationDetails();
    }
  }, [visible, stationId, establishmentId, stationAccount]);

  useEffect(() => {
    if (!visible) return;
    setActiveStep(0);
    const selected = formValues?.stations_field || [];
    if (selected.length > 0 || !change) return;
    const userStations = getUserStations(auth);
    const current = userStations.find((station) => station.id === stationId) || userStations[0];
    if (current) {
      change('stations_field', [{ id: current.id, title: current.name || current.title }]);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const loadCategories = async () => {
      const stations = getUserStations(auth);
      if (!stations.length) {
        setCategoryOptions([]);
        return;
      }
      try {
        const token = JSON.parse(localStorage.getItem(USER_TOKEN));
        const results = await Promise.all(
          stations.map((station) =>
            axios
              .get(`${BASE_API}/categories/?where[station_id]=${station.id}`, {
                headers: { Authorization: `${token}` },
              })
              .then((response) => ({
                station,
                categories: response.data?.content || response.data || [],
              }))
              .catch(() => ({ station, categories: [] }))
          )
        );
        const options = [];
        results.forEach(({ station, categories }) => {
          (Array.isArray(categories) ? categories : []).forEach((category) => {
            options.push({
              id: category.id,
              title: station.name
                ? `${category.name || category.title} (${station.name})`
                : category.name || category.title,
            });
          });
        });
        setCategoryOptions(options);
      } catch (error) {
        console.warn('Erro ao carregar categorias:', error);
        setCategoryOptions([]);
      }
    };
    loadCategories();
  }, [visible, auth]);

  const handleNext = () => {
    if (activeStep === 0) {
      if (formValues?.batch_mode) {
        if (!Array.isArray(formValues?.batch_customers) || formValues.batch_customers.length === 0) {
          setErrorMessage('Importe um arquivo com pelo menos um cliente válido para gerar links em massa');
          setShowErrorDialog(true);
          return;
        }

        const hasActivityFallback = Boolean((formValues?.service_name || '').trim());
        const hasMissingActivity = formValues.batch_customers.some(
          (customer) => !String(customer?.service_name || '').trim()
        );

        if (!hasActivityFallback && hasMissingActivity) {
          setErrorMessage('Informe a atividade/serviço no formulário ou preencha a coluna atividade no arquivo para todos os clientes');
          setShowErrorDialog(true);
          return;
        }
      } else if (!formValues?.service_name) {
        setErrorMessage('Por favor, informe a atividade ou serviço');
        setShowErrorDialog(true);
        return;
      }

      if (!formValues?.batch_mode && (!formValues?.customer_name || !formValues?.customer_cpf)) {
        console.log('Validação falhou:', {
          service_name: formValues?.service_name,
          customer_name: formValues?.customer_name,
          customer_cpf: formValues?.customer_cpf
        });
        setErrorMessage('Por favor, preencha todos os campos obrigatórios');
        setShowErrorDialog(true);
        return;
      }
    }

    if (activeStep === 1) {
      if (formValues?.has_partner) {
        if (!formValues?.partner_id) {
          setErrorMessage('Por favor, selecione um parceiro');
          setShowErrorDialog(true);
          return;
        }
        if (!formValues?.partner_recipient_id) {
          setErrorMessage('Por favor, selecione uma conta bancária do parceiro');
          setShowErrorDialog(true);
          return;
        }
      }
    }

    if (activeStep === 2) {
      if (!formValues?.link_value || formValues.link_value <= 0) {
        setErrorMessage('Por favor, informe um valor válido para o link');
        setShowErrorDialog(true);
        return;
      }

      if (!formValues?.payment_methods || formValues.payment_methods.length === 0) {
        setErrorMessage('Por favor, selecione pelo menos uma forma de pagamento');
        setShowErrorDialog(true);
        return;
      }

      if (formValues?.has_partner) {
        const total = (formValues?.partner_percentage || 0) + (formValues?.station_percentage || 0);
        if (total !== 100) {
          setErrorMessage('A soma dos percentuais deve ser 100%');
          setShowErrorDialog(true);
          return;
        }
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const published = await upsertLinkPagamentoActivity(
        {
          ...formValues,
          id: formValues?.id || activityId,
          body_name: formValues?.service_name || formValues?.body_name || formValues?.name,
          body_description:
            formValues?.body_description ||
            formValues?.description ||
            formValues?.custom_message ||
            formValues?.service_name,
        },
        auth
      );
      const activityIdForLink = published?.parent_action_id || published?.id || formValues?.id || activityId;
      if (activityIdForLink && change) {
        change('id', activityIdForLink);
      }

      if (formValues?.batch_mode) {
        const batchPayload = {
          customers: formValues?.batch_customers || [],
          service_name: formValues?.service_name || '',
          link_value: parseFloat(formValues?.link_value) || 0,
          payment_methods: formValues?.payment_methods || [],
          allow_installments: formValues?.allow_installments || false,
          has_partner: formValues?.has_partner || false,
          station_percentage: parseFloat(formValues?.station_percentage) || 100,
          partner_percentage: parseFloat(formValues?.partner_percentage) || 0,
          partner_recipient_id: formValues?.partner_recipient_id || null,
          partner_user_id: formValues?.partner_user_id || null,
          partner_name: formValues?.partner_name || null,
          station_id: stationId,
          establishment_id: establishmentId,
          activity_id: activityIdForLink || null,
          recurrence_type: formValues?.recurrence_type || 'single',
          custom_message: formValues?.custom_message || '',
          send_email: formValues?.send_email !== false,
          send_whatsapp: formValues?.send_whatsapp !== false,
        };

        const batchResponse = await axios.post(
          `${BASE_API}/payment-links/generate-batch`,
          batchPayload,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        );

        if (batchResponse.data?.total >= 0) {
          setBatchResult(batchResponse.data);
          setShowBatchResultDialog(true);

          if (batchResponse.data?.success && onClickProgress) {
            await onClickProgress({ ...formValues, id: activityIdForLink }, batchResponse.data);
          }
        } else {
          setErrorMessage(batchResponse.data?.message || 'Erro ao gerar links em massa.');
          setShowErrorDialog(true);
        }

        return;
      }
      
      const payload = {
        // Dados do cliente/pagador
        customer_name: formValues?.customer_name,
        customer_cpf: formValues?.customer_cpf,
        customer_email: formValues?.customer_email,
        customer_phone: formValues?.customer_phone,
        // Dados do link
        service_name: formValues?.service_name,
        link_value: parseFloat(formValues?.link_value) || 0,
        payment_methods: formValues?.payment_methods || [],
        allow_installments: formValues?.allow_installments || false,
        // Dados de split (parceiro)
        has_partner: formValues?.has_partner || false,
        station_percentage: parseFloat(formValues?.station_percentage) || 100,
        partner_percentage: parseFloat(formValues?.partner_percentage) || 0,
        partner_recipient_id: formValues?.partner_recipient_id || null,
        partner_user_id: formValues?.partner_user_id || null,
        partner_name: formValues?.partner_name || null,
        // IDs
        station_id: stationId,
        establishment_id: establishmentId,
        activity_id: activityIdForLink || null,
        // Recorrência
        recurrence_type: formValues?.recurrence_type || 'single',
        custom_message: formValues?.custom_message || '',
        send_email: formValues?.send_email !== false,
        send_whatsapp: formValues?.send_whatsapp !== false,
      };

      console.log('Enviando payload para gerar link:', payload);

      const response = await axios.post(
        `${BASE_API}/payment-links/generate`,
        payload,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.data?.success && response.data?.payment_link) {
        setGeneratedLinkData(response.data.payment_link);
        setShowLinkGeradoModal(true);
        
        // Chamar callback se existir
        if (onClickProgress) {
          await onClickProgress({ ...formValues, id: activityIdForLink }, response.data.payment_link);
        }
      } else {
        setErrorMessage(response.data?.message || 'Erro ao gerar link de pagamento.');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('Erro ao gerar link:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Erro ao gerar link de pagamento.';
      setErrorMessage(errorMsg);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseLinkGeradoModal = () => {
    setShowLinkGeradoModal(false);
    setGeneratedLinkData(null);
    onClose();
  };

  const handleCloseBatchResultDialog = () => {
    setShowBatchResultDialog(false);
    setBatchResult(null);
    onClose();
  };

  const stationOptions = getUserStations(auth).map((station) => ({
    id: station.id,
    title: station.name || station.title,
  }));

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Box style={{ marginBottom: '16px' }}>
              <Field
                component={SelectMultipleV4}
                label="Categoria"
                name="categories_field"
                options={categoryOptions}
                helperText="Necessário para a atividade aparecer no feed das stations."
              />
            </Box>
            <Box style={{ marginBottom: '16px' }}>
              <Field
                component={SelectMultipleV4}
                label="Publicar nas Stations"
                name="stations_field"
                options={stationOptions}
                helperText="A atividade aparece no feed das stations selecionadas. O Clubs continua controlando edição e pausa."
              />
            </Box>
            <DadosPessoais formValues={formValues} change={change} />
          </>
        );
      case 1:
        return (
          <DadosParceiro
            formValues={formValues}
            change={change}
            stationId={stationId}
            stationIds={availableStationIds}
          />
        );
      case 2:
        return <Valores formValues={formValues} change={change} stationAccount={stationAccountDetails || stationAccount} />;
      case 3:
        return <Confirmacao formValues={formValues} onGenerateLink={handleGenerateLink} />;
      default:
        return 'Etapa desconhecida';
    }
  };

  return (
    <>
      <AlertDialogSlide
        visible={visible}
        onClose={onClose}
        title={editMode ? 'Editar Link de Pagamento' : 'Link de pagamento'}
        maxWidth="md"
        height="600px"
      >
        <form onSubmit={handleSubmit}>
          <Stepper activeStep={activeStep} alternativeLabel style={{ marginBottom: '24px' }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>
                  {label}
                  <Typography variant="caption" display="block">
                    {index + 1} de {steps.length}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box>{getStepContent(activeStep)}</Box>

        {activeStep !== 3 && (
          <Box style={{ padding: '16px 0', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={loading}
            >
              Voltar
            </Button>
            <Box>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  style={{ marginRight: '8px' }}
                  disabled={loading}
                >
                  Anterior
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={loading}
              >
                {activeStep === steps.length - 1 ? 'Finalizar' : 'Salvar e continuar'}
              </Button>
            </Box>
          </Box>
        )}
        </form>
      </AlertDialogSlide>

      <AlertDialogSlide
        visible={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Aviso"
        maxWidth="sm"
      >
        <Typography>{errorMessage}</Typography>
        <Box style={{ marginTop: '16px', textAlign: 'right' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowErrorDialog(false)}
          >
            OK
          </Button>
        </Box>
      </AlertDialogSlide>

      <LinkGeradoModal
        open={showLinkGeradoModal}
        onClose={handleCloseLinkGeradoModal}
        paymentLinkData={generatedLinkData}
      />

      <AlertDialogSlide
        visible={showBatchResultDialog}
        onClose={handleCloseBatchResultDialog}
        title="Resultado da geração em massa"
        maxWidth="sm"
      >
        <Typography>
          {batchResult?.message || 'Processamento concluído.'}
        </Typography>
        <Box style={{ marginTop: '16px' }}>
          <Typography><strong>Total processado:</strong> {batchResult?.total || 0}</Typography>
          <Typography><strong>Sucesso:</strong> {batchResult?.success_count || 0}</Typography>
          <Typography><strong>Falhas:</strong> {batchResult?.failure_count || 0}</Typography>
        </Box>
        {Array.isArray(batchResult?.results) && batchResult.results.some((item) => item.status === 'error') && (
          <Box style={{ marginTop: '16px', maxHeight: '220px', overflow: 'auto' }}>
            {batchResult.results
              .filter((item) => item.status === 'error')
              .slice(0, 10)
              .map((item, index) => (
                <Typography key={`${item.customer_name || 'erro'}-${index}`} style={{ marginBottom: '8px' }}>
                  {item.customer_name || 'Cliente'}: {item.message}
                </Typography>
              ))}
          </Box>
        )}
        <Box style={{ marginTop: '16px', textAlign: 'right' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseBatchResultDialog}
          >
            OK
          </Button>
        </Box>
      </AlertDialogSlide>
    </>
  );
};

LinkPagamentoModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onClickProgress: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  stationId: PropTypes.number,
  stationAccount: PropTypes.object,
  establishmentId: PropTypes.number,
  availableStationIds: PropTypes.arrayOf(PropTypes.number),
  activityId: PropTypes.number,
  auth: PropTypes.object,
};

const mapStateToProps = (state) => {
  const formValues = state.form?.linkPagamentoForm?.values || {};
  const authUser = state.auth?.user;
  const establishment = authUser?.establishments?.[0] || null;
  const selectedStation = establishment?.stations?.[0] || null;
  const stationId = selectedStation?.id || null;
  const stationAccount = establishment?.digital_account || null;
  const availableStationIds = getAvailableStationIds(authUser);
  
  return {
    formValues,
    stationId,
    stationAccount,
    establishmentId: selectedStation?.establishment_id || establishment?.id || null,
    availableStationIds,
    auth: state.auth,
  };
};

export default compose(
  connect(mapStateToProps),
  reduxForm({
    form: 'linkPagamentoForm',
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
  })
)(LinkPagamentoModal);
